import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import type { ScriptExecution } from '../../types';
import { getApiErrorMessage } from '../../utils/errors';

export interface CallScriptSummary {
  id: string;
  name: string;
  eventType: string;
  timeoutSeconds: number;
  delaySeconds: number;
}

type ExecuteState = 'idle' | 'sending' | 'success' | 'error';

interface UseCallScriptsParams {
  callId: string;
  enabled: boolean;
}

interface UseCallScriptsResult {
  callScripts: CallScriptSummary[];
  scriptExecutions: ScriptExecution[];
  scriptsLoading: boolean;
  scriptsError: string | null;
  selectedScriptId: string;
  executeState: ExecuteState;
  executeError: string | null;
  setSelectedScriptId: (value: string) => void;
  executeScript: () => Promise<void>;
}

export function useCallScripts({ callId, enabled }: UseCallScriptsParams): UseCallScriptsResult {
  const { t } = useTranslation(['components']);
  const [callScripts, setCallScripts] = useState<CallScriptSummary[]>([]);
  const [scriptExecutions, setScriptExecutions] = useState<ScriptExecution[]>([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [scriptsError, setScriptsError] = useState<string | null>(null);
  const [selectedScriptId, setSelectedScriptIdState] = useState<string>('');
  const [executeState, setExecuteState] = useState<ExecuteState>('idle');
  const [executeError, setExecuteError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;

    const fetchCallScripts = async () => {
      setScriptsLoading(true);
      setScriptsError(null);
      try {
        const response = await api.get(`/api/calls/${callId}/scripts/`);
        if (!mounted) return;
        const availableScripts = (response.data?.availableScripts || response.data?.available_scripts || []) as CallScriptSummary[];
        const executions = (response.data?.executions || []) as ScriptExecution[];
        setCallScripts(availableScripts);
        setScriptExecutions(executions);
        setSelectedScriptIdState((prev) => {
          if (prev && availableScripts.some((script) => script.id === prev)) return prev;
          return availableScripts[0]?.id || '';
        });
      } catch (error: unknown) {
        if (!mounted) return;
        setScriptsError(getApiErrorMessage(error, t('components:callDetails.scripts.fetchError')));
      } finally {
        if (mounted) setScriptsLoading(false);
      }
    };

    fetchCallScripts();
    return () => {
      mounted = false;
    };
  }, [callId, enabled, t]);

  const setSelectedScriptId = useCallback((value: string) => {
    setSelectedScriptIdState(value);
    setExecuteState('idle');
    setExecuteError(null);
  }, []);

  const executeScript = useCallback(async () => {
    if (!selectedScriptId) {
      setExecuteError(t('components:callDetails.scripts.selectRequired'));
      return;
    }
    setExecuteState('sending');
    setExecuteError(null);
    try {
      const response = await api.post(`/api/calls/${callId}/scripts/execute/`, { scriptId: selectedScriptId });
      const execution = response.data as ScriptExecution;
      setScriptExecutions((prev) => [execution, ...prev.filter((item) => item.id !== execution.id)]);
      setExecuteState('success');
    } catch (error: unknown) {
      setExecuteState('error');
      setExecuteError(getApiErrorMessage(error, t('components:callDetails.scripts.executeError')));
    }
  }, [callId, selectedScriptId, t]);

  return useMemo(
    () => ({
      callScripts,
      scriptExecutions,
      scriptsLoading,
      scriptsError,
      selectedScriptId,
      executeState,
      executeError,
      setSelectedScriptId,
      executeScript,
    }),
    [
      callScripts,
      scriptExecutions,
      scriptsLoading,
      scriptsError,
      selectedScriptId,
      executeState,
      executeError,
      setSelectedScriptId,
      executeScript,
    ],
  );
}
